import { Chip, CircularProgress, Popover, Stack, Typography } from "@mui/material";
import Grid from "@mui/material/Grid";
import { useEffect, useState } from "react";

import {
	fetchJobylonStages,
	JobylonStage,
	jobylonCandidateUrl,
	moveJobylonCandidate,
} from "../../api/assignments/jobylon-jobs";
import { PersonAssignmentRetrieveType } from "../../api/assignments/person-assignment";
import { JobylonBadge } from "../shared/jobylon-badge";

interface JobylonStageCellProps {
	pa: PersonAssignmentRetrieveType;
	/** ODI's row id for the Jobylon job behind this role. */
	jobylonJobId: number;
	/** This job's candidate board in Jobylon's own UI. */
	jobylonBoardUrl?: string;
	/** Called once the stage has changed, so the row can be re-read. */
	onMoved: () => void;
}

/**
 * Where Jobylon holds this person on this role, and the way to move them.
 *
 * Shown instead of ODI's own column and status, not beside them: for a role
 * whose pipeline lives in Jobylon, ODI's column is always the same placeholder
 * and saying both only invites the question of which one is true.
 *
 * The stage list is read from the customer's feed when the popover opens, so a
 * recruiter picks from the pipeline as it stands rather than a copy that may
 * have drifted.
 */
export const JobylonStageCell = ({
	pa,
	jobylonJobId,
	jobylonBoardUrl,
	onMoved,
}: JobylonStageCellProps) => {
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
	const [stages, setStages] = useState<JobylonStage[] | null>(null);
	const [isMoving, setIsMoving] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const canMove = pa.jobylon_can_move === true;
	// This cell is about one person, so the link lands on them rather than on a
	// board they then have to find themselves in.
	const candidateUrl = jobylonCandidateUrl(jobylonBoardUrl, pa.person_name);

	useEffect(() => {
		if (!anchorEl || !canMove) return;
		const controller = new AbortController();
		setError(null);
		fetchJobylonStages(jobylonJobId, controller.signal)
			.then(setStages)
			.catch((err: unknown) => {
				if (err instanceof DOMException && err.name === "AbortError") return;
				setError(err instanceof Error ? err.message : "Could not load stages");
			});
		return () => controller.abort();
	}, [anchorEl, canMove, jobylonJobId]);

	const handleMove = (statusId: number) => {
		setIsMoving(true);
		setError(null);
		moveJobylonCandidate(jobylonJobId, pa.person, statusId)
			.then(() => {
				setAnchorEl(null);
				onMoved();
			})
			.catch((err: unknown) => {
				setError(
					err instanceof Error ? err.message : "Jobylon refused the move"
				);
			})
			.finally(() => setIsMoving(false));
	};

	return (
		<>
			<Stack
				direction="row"
				spacing={0.75}
				alignItems="center"
				data-component="person-assignment-jobylon-stage"
			>
				<Typography
					variant="body2"
					sx={{
						cursor: "pointer",
						"&:hover": { textDecoration: "underline" },
					}}
					onClick={(event) => setAnchorEl(event.currentTarget)}
				>
					{pa.jobylon_stage || "Stage not read yet"}
				</Typography>
				<JobylonBadge
					height={12}
					href={candidateUrl}
					title="Stage in Jobylon — open this candidate there"
				/>
			</Stack>
			<Popover
				data-component="jobylon-stage-popover"
				anchorEl={anchorEl}
				onClose={() => setAnchorEl(null)}
				open={Boolean(anchorEl)}
			>
				<Stack spacing={1} sx={{ p: 1.5, maxWidth: 400 }}>
					{canMove ? (
						<Typography sx={{ fontWeight: "bold" }}>
							Move in Jobylon
						</Typography>
					) : (
						// Jobylon's Push API only accepts updates to applications
						// ODI created, so there is nothing to offer here.
						<Typography variant="body2" color="text.secondary">
							They applied through Jobylon, so only Jobylon can move them.{" "}
							<Typography
								component="a"
								variant="body2"
								href={candidateUrl}
								target="_blank"
								rel="noopener"
								data-component="jobylon-open-candidate"
								sx={{ color: "primary.main" }}
							>
								Open them in Jobylon
							</Typography>{" "}
							to change their stage.
						</Typography>
					)}
					{error && (
						<Typography variant="body2" color="error.main">
							{error}
						</Typography>
					)}
					{canMove && stages === null && !error && (
						<CircularProgress size={20} />
					)}
					{canMove && stages !== null && (
						<Grid container spacing={1}>
							{stages.map((stage) => (
								<Grid key={stage.status_id}>
									<Chip
										data-component="jobylon-stage-chip"
										disabled={isMoving}
										variant={
											stage.name === pa.jobylon_stage
												? "outlined"
												: "filled"
										}
										label={stage.name}
										onClick={() => handleMove(stage.status_id)}
									/>
								</Grid>
							))}
						</Grid>
					)}
				</Stack>
			</Popover>
		</>
	);
};

export default JobylonStageCell;
